import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { PageHeader, Card, CardTitle, Badge, Btn, Modal, FormGroup, DataTable, Td, Tr, Avatar, Toggle, Divider, SearchBar } from '../components/UI';

/* ─── Permission definitions ─────────────────────────────── */
const ALL_PERMS = [
  { key:'customers.view',     label:'View Customers',       group:'Customers', desc:'Browse and search customer records' },
  { key:'customers.create',   label:'Add Customers',        group:'Customers', desc:'Register new LPG customers' },
  { key:'customers.edit',     label:'Edit Customers',       group:'Customers', desc:'Modify existing customer details' },
  { key:'customers.delete',   label:'Delete Customers',     group:'Customers', desc:'Permanently remove customer records' },
  { key:'bookings.view',      label:'View Bookings',        group:'Bookings',  desc:'Browse booking records and history' },
  { key:'bookings.create',    label:'Create Bookings',      group:'Bookings',  desc:'Register new gas cylinder bookings' },
  { key:'bookings.dispatch',  label:'Dispatch Bookings',    group:'Bookings',  desc:'Mark bookings as Out for Delivery' },
  { key:'bookings.deliver',   label:'Confirm Delivery',     group:'Bookings',  desc:'Mark bookings as delivered' },
  { key:'bookings.delete',    label:'Delete Bookings',      group:'Bookings',  desc:'Remove booking records' },
  { key:'notifications.view', label:'View Rebook Alerts',   group:'Alerts',    desc:'See 30-day rebooking notifications' },
  { key:'reports.view',       label:'View Reports',         group:'Analytics', desc:'Access analytics and revenue reports' },
  { key:'users.view',         label:'View Users',           group:'System',    desc:'See user account listing' },
  { key:'users.create',       label:'Create Users',         group:'System',    desc:'Register new system users' },
  { key:'users.edit',         label:'Edit Users',           group:'System',    desc:'Modify user accounts and roles' },
  { key:'users.delete',       label:'Delete Users',         group:'System',    desc:'Remove user accounts' },
  { key:'roles.manage',       label:'Manage Roles',         group:'System',    desc:'Create, edit and assign roles' },
];

const GROUPS = [...new Set(ALL_PERMS.map(p => p.group))];

const DEFAULT_ROLES = {
  super:    { label:'Super Admin',   color:'#f97316', icon:'⭐', description:'Full unrestricted system access',                    perms: ALL_PERMS.map(p=>p.key) },
  admin:    { label:'Administrator', color:'#3b82f6', icon:'🛡️', description:'Manage operations, bookings and reports',            perms: ALL_PERMS.filter(p=>!['users.delete','roles.manage'].includes(p.key)).map(p=>p.key) },
  employee: { label:'Employee',      color:'#22c55e', icon:'👤', description:'Day-to-day booking and customer operations',         perms: ['customers.view','customers.create','customers.edit','bookings.view','bookings.create','notifications.view'] },
};

const EMPTY_USER = { name:'', username:'', email:'', password:'', role:'employee', isActive:true };

export default function Users() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('users');
  const [search, setSearch]     = useState('');
  const [modal, setModal]       = useState(null);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(EMPTY_USER);
  const [saving, setSaving]     = useState(false);

  const [roles, setRoles] = useState(() => {
    try { return JSON.parse(localStorage.getItem('indiaoil_roles')) || DEFAULT_ROLES; }
    catch { return DEFAULT_ROLES; }
  });
  const [roleModal, setRoleModal] = useState(null);
  const [roleKey, setRoleKey]     = useState(null);
  const [roleForm, setRoleForm]   = useState({});
  const [roleDirty, setRoleDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.getUsers(); setUsers(data); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  /* ─── User helpers ──────────────────────────────── */
  const openAdd  = () => { setEditId(null); setForm(EMPTY_USER); setModal('user'); };
  const openEdit = u  => {
    setEditId(u.id);
    setForm({ name:u.name, username:u.username, email:u.email, password:'', role:u.role, isActive:u.isActive });
    setModal('user');
  };

  const saveUser = async () => {
    if (!form.name || !form.username || !form.email) return toast.error('Name, username and email required');
    if (!editId && !form.password) return toast.error('Password required for new users');
    setSaving(true);
    try {
      const payload = { ...form };
      if (editId && !payload.password) delete payload.password;
      if (editId) { await api.updateUser(editId, payload); toast.success('User updated'); }
      else        { await api.createUser(payload); toast.success('User created'); }
      setModal(null); load();
    } catch(err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const removeUser = async id => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.deleteUser(id); toast.success('User deleted'); load(); }
    catch(err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  /* ─── Role helpers ──────────────────────────────── */
  const openRoleEdit = key => {
    setRoleKey(key);
    setRoleForm({ ...roles[key], perms:[...roles[key].perms] });
    setRoleModal('edit'); setRoleDirty(false);
  };
  const openRoleNew = () => {
    const key = 'custom_' + Date.now();
    setRoleKey(key);
    setRoleForm({ label:'New Role', color:'#06b6d4', icon:'◎', description:'', perms:[] });
    setRoleModal('new'); setRoleDirty(false);
  };
  const saveRole = () => {
    if (!roleForm.label) return toast.error('Role name required');
    const updated = { ...roles, [roleKey]: roleForm };
    setRoles(updated);
    localStorage.setItem('indiaoil_roles', JSON.stringify(updated));
    toast.success(`Role "${roleForm.label}" saved`);
    setRoleModal(null); setRoleDirty(false);
  };
  const deleteRole = key => {
    if (['super','admin','employee'].includes(key)) return toast.error('Cannot delete built-in roles');
    if (!window.confirm('Delete this role?')) return;
    const updated = { ...roles };
    delete updated[key];
    setRoles(updated);
    localStorage.setItem('indiaoil_roles', JSON.stringify(updated));
    toast.success('Role deleted');
  };
  const resetRoles = () => {
    if (!window.confirm('Reset all roles to defaults?')) return;
    setRoles(DEFAULT_ROLES);
    localStorage.removeItem('indiaoil_roles');
    toast.success('Roles reset to defaults');
  };
  const togglePerm  = key => { setRoleDirty(true); setRoleForm(r => ({ ...r, perms: r.perms.includes(key) ? r.perms.filter(p=>p!==key) : [...r.perms, key] })); };
  const toggleGroup = group => {
    const gp = ALL_PERMS.filter(p=>p.group===group).map(p=>p.key);
    const allOn = gp.every(k => roleForm.perms.includes(k));
    setRoleDirty(true);
    setRoleForm(r => ({ ...r, perms: allOn ? r.perms.filter(p=>!gp.includes(p)) : [...new Set([...r.perms,...gp])] }));
  };
  const RF = (k, v) => { setRoleForm(r => ({...r,[k]:v})); setRoleDirty(true); };
  const F  = (k, v) => setForm(f => ({...f,[k]:v}));
  const roleBadge = r => ({ super:'brand', admin:'info', employee:'success' }[r] || 'purple');

  const filteredUsers = users.filter(u =>
    !search || u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage system users and configure role-based access permissions" />

      {/* Tabs — pill style */}
      <div className="au d1" style={{ display:'flex', gap:4, background:'var(--bg-surface)', padding:4, borderRadius:'var(--radius-lg)', border:'1px solid var(--border-subtle)', width:'fit-content', marginBottom:22, flexWrap:'wrap' }}>
        {[['users','👤 Users'],['roles','🔐 Roles & Permissions']].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)} style={{
            padding:'8px 20px', borderRadius:'var(--radius-md)', border:'none',
            background: tab===v ? 'var(--bg-elevated)' : 'transparent',
            color: tab===v ? 'var(--text-primary)' : 'var(--text-muted)',
            fontSize:13, fontWeight:600, cursor:'pointer', transition:'var(--transition)',
            boxShadow: tab===v ? 'var(--shadow-sm)' : 'none',
          }}>{l}</button>
        ))}
      </div>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <>
          <div className="au d1" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, gap:10, flexWrap:'wrap' }}>
            <SearchBar value={search} onChange={setSearch} placeholder="Search users..." />
            <Btn onClick={openAdd} icon="＋">Add User</Btn>
          </div>
          <Card noPad className="au d2">
            <DataTable loading={loading} empty={filteredUsers.length===0 ? 'No users found' : null}
              headers={['User','Username','Email','Role','Status','Actions']}>
              {filteredUsers.map(u => (
                <Tr key={u.id}>
                  <Td>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <Avatar name={u.name} size={34} color={roles[u.role]?.color} />
                      <div>
                        <div style={{ fontSize:13.5, fontWeight:600 }}>{u.name}</div>
                        <div style={{ fontSize:11.5, color:'var(--text-muted)' }}>{roles[u.role]?.icon} {roles[u.role]?.label}</div>
                      </div>
                    </div>
                  </Td>
                  <Td><span style={{ fontFamily:'var(--font-mono)', fontSize:12.5, color:'var(--text-secondary)' }}>{u.username}</span></Td>
                  <Td style={{ fontSize:12.5, color:'var(--text-secondary)' }}>{u.email}</Td>
                  <Td><Badge type={roleBadge(u.role)}>{roles[u.role]?.label || u.role}</Badge></Td>
                  <Td>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <div style={{ width:7, height:7, borderRadius:'50%', background: u.isActive ? 'var(--accent-green)' : 'var(--accent-red)' }} />
                      <span style={{ fontSize:12.5, color: u.isActive ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight:600 }}>{u.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </Td>
                  <Td>
                    <div style={{ display:'flex', gap:6 }}>
                      <Btn size="xs" variant="secondary" onClick={() => openEdit(u)}>Edit</Btn>
                      <Btn size="xs" variant="danger"    onClick={() => removeUser(u.id)}>✕</Btn>
                    </div>
                  </Td>
                </Tr>
              ))}
            </DataTable>
          </Card>
        </>
      )}

      {/* ── ROLES TAB ── */}
      {tab === 'roles' && (
        <div>
          <div className="au d1" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
            <div style={{ fontSize:13, color:'var(--text-muted)' }}>
              {Object.keys(roles).length} roles defined
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Btn variant="ghost" size="sm" onClick={resetRoles}>↺ Reset Defaults</Btn>
              <Btn onClick={openRoleNew} icon="＋">New Role</Btn>
            </div>
          </div>

          {/* Role cards — auto-fit responsive grid */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:14, marginBottom:24 }}>
            {Object.entries(roles).map(([key, role], i) => {
              const userCount = users.filter(u => u.role === key).length;
              const isBuiltIn = ['super','admin','employee'].includes(key);
              return (
                <div key={key} className="au" style={{ animationDelay:`${i*0.06}s`,
                  background:'var(--bg-surface)',
                  border:`1px solid ${key==='super' ? 'rgba(249,115,22,.2)' : 'var(--border-subtle)'}`,
                  borderRadius:'var(--radius-lg)', padding:'18px 20px',
                  transition:'var(--transition)', cursor:'pointer',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-surface)'}
                onClick={() => openRoleEdit(key)}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                    <div style={{ width:40, height:40, borderRadius:'var(--radius-md)', background:`${role.color}18`, border:`1px solid ${role.color}30`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>
                      {role.icon}
                    </div>
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      {isBuiltIn && <Badge type="gray">Built-in</Badge>}
                      <button onClick={e => { e.stopPropagation(); deleteRole(key); }}
                        style={{ background:'transparent', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:13, padding:'2px 5px', borderRadius:4 }}>✕</button>
                    </div>
                  </div>
                  <div style={{ fontSize:15, fontWeight:700, color:'var(--text-primary)', marginBottom:4 }}>{role.label}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:14, lineHeight:1.5 }}>{role.description || 'No description'}</div>
                  <div style={{ display:'flex', gap:14, fontSize:12 }}>
                    <span style={{ color:'var(--text-muted)' }}><strong style={{ color:role.color, fontFamily:'var(--font-mono)' }}>{role.perms.length}</strong> permissions</span>
                    <span style={{ color:'var(--text-muted)' }}><strong style={{ color:role.color, fontFamily:'var(--font-mono)' }}>{userCount}</strong> user{userCount!==1?'s':''}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Permission matrix — scrollable on mobile */}
          <Card noPad className="au d4">
            <div style={{ padding:'18px 22px 0' }}>
              <CardTitle>Permission Matrix</CardTitle>
            </div>
            <div style={{ overflowX:'auto', WebkitOverflowScrolling:'touch' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5, minWidth:600 }}>
                <thead>
                  <tr>
                    <th style={{ padding:'10px 22px', textAlign:'left', fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.06em', borderBottom:'1px solid var(--border-subtle)', background:'var(--bg-deep)', whiteSpace:'nowrap' }}>Permission</th>
                    {Object.entries(roles).map(([key, role]) => (
                      <th key={key} style={{ padding:'10px 16px', fontSize:11, fontWeight:700, color:role.color||'var(--text-muted)', textAlign:'center', textTransform:'uppercase', letterSpacing:'.06em', borderBottom:'1px solid var(--border-subtle)', background:'var(--bg-deep)', whiteSpace:'nowrap' }}>
                        {role.icon} {role.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {GROUPS.map(group => (
                    <React.Fragment key={group}>
                      <tr>
                        <td colSpan={Object.keys(roles).length + 1} style={{ padding:'12px 22px 6px', fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.06em', background:'var(--bg-deep)', borderTop:'1px solid var(--border-subtle)' }}>
                          {group}
                        </td>
                      </tr>
                      {ALL_PERMS.filter(p => p.group === group).map(perm => (
                        <tr key={perm.key} style={{ borderBottom:'1px solid var(--border-subtle)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <td style={{ padding:'10px 22px' }}>
                            <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)' }}>{perm.label}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>{perm.desc}</div>
                          </td>
                          {Object.entries(roles).map(([key, role]) => (
                            <td key={key} style={{ textAlign:'center', padding:'10px 16px' }}>
                              {role.perms.includes(perm.key) ? (
                                <span style={{ display:'inline-flex', width:20, height:20, borderRadius:'50%', background:'var(--accent-green-dim)', border:'1px solid rgba(34,197,94,.2)', alignItems:'center', justifyContent:'center', fontSize:11, color:'var(--accent-green)' }}>✓</span>
                              ) : (
                                <span style={{ display:'inline-flex', width:20, height:20, borderRadius:'50%', background:'var(--bg-elevated)', alignItems:'center', justifyContent:'center', fontSize:10, color:'var(--text-muted)' }}>—</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ── USER MODAL ── */}
      {modal === 'user' && (
        <Modal title={editId ? 'Edit User' : 'Create New User'} subtitle={editId ? 'Update user account details' : 'Set up a new system user account'} onClose={() => setModal(null)}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14 }}>
              <FormGroup label="Full Name" required><input value={form.name} onChange={e=>F('name',e.target.value)} placeholder="Full name" className="form-input" /></FormGroup>
              <FormGroup label="Username" required><input value={form.username} onChange={e=>F('username',e.target.value)} placeholder="Login username" className="form-input" disabled={!!editId} /></FormGroup>
              <FormGroup label="Email Address" required><input type="email" value={form.email} onChange={e=>F('email',e.target.value)} placeholder="user@example.com" className="form-input" /></FormGroup>
              <FormGroup label={editId ? 'New Password (blank = no change)' : 'Password'} required={!editId}>
                <input type="password" value={form.password} onChange={e=>F('password',e.target.value)} placeholder="••••••••" className="form-input" />
              </FormGroup>
              <FormGroup label="Role">
                <select value={form.role} onChange={e=>F('role',e.target.value)} className="form-select">
                  {Object.entries(roles).map(([key,r]) => <option key={key} value={key}>{r.icon} {r.label}</option>)}
                </select>
              </FormGroup>
              <FormGroup label="Status">
                <div style={{ display:'flex', alignItems:'center', gap:12, paddingTop:10 }}>
                  <Toggle checked={form.isActive} onChange={v=>F('isActive',v)} />
                  <span style={{ fontSize:13, color:form.isActive?'var(--accent-green)':'var(--text-muted)', fontWeight:600 }}>{form.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </FormGroup>
            </div>

            {form.role && roles[form.role] && (
              <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-md)', padding:'12px 14px' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', letterSpacing:'.05em', marginBottom:8 }}>ROLE PERMISSIONS PREVIEW</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                  {roles[form.role].perms.slice(0,8).map(k => {
                    const p = ALL_PERMS.find(x=>x.key===k);
                    return p ? <Badge key={k} type="gray">{p.label}</Badge> : null;
                  })}
                  {roles[form.role].perms.length > 8 && <Badge type="gray">+{roles[form.role].perms.length-8} more</Badge>}
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:10, marginTop:8, justifyContent:'flex-end' }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn onClick={saveUser} disabled={saving}>{saving ? 'Saving…' : editId ? 'Update User' : 'Create User'}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── ROLE EDIT MODAL ── */}
      {(roleModal === 'edit' || roleModal === 'new') && roleForm && (
        <Modal title={roleModal==='new' ? 'Create New Role' : 'Edit Role'} subtitle="Configure role details and set granular permissions" onClose={() => setRoleModal(null)} maxWidth={680}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px', gap:14, marginBottom:20 }}>
            <FormGroup label="Role Name" required><input value={roleForm.label} onChange={e=>RF('label',e.target.value)} placeholder="e.g. Supervisor" className="form-input" /></FormGroup>
            <FormGroup label="Description"><input value={roleForm.description||''} onChange={e=>RF('description',e.target.value)} placeholder="What this role can do" className="form-input" /></FormGroup>
            <FormGroup label="Icon"><input value={roleForm.icon||''} onChange={e=>RF('icon',e.target.value)} placeholder="⭐" className="form-input" style={{ textAlign:'center', fontSize:20 }} /></FormGroup>
          </div>

          {/* Color picker */}
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', letterSpacing:'.05em', marginBottom:10 }}>ROLE COLOR</div>
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
              {['#f97316','#3b82f6','#22c55e','#a855f7','#06b6d4','#f59e0b','#ef4444','#ec4899','#8b5cf6'].map(c => (
                <button key={c} onClick={() => RF('color',c)} style={{
                  width:28, height:28, borderRadius:'50%', background:c, cursor:'pointer',
                  border:`3px solid ${roleForm.color===c ? 'white' : 'transparent'}`,
                  boxShadow: roleForm.color===c ? `0 0 0 2px ${c}` : 'none',
                  transition:'var(--transition)',
                }} />
              ))}
              <input type="color" value={roleForm.color||'#3b82f6'} onChange={e=>RF('color',e.target.value)}
                style={{ width:28, height:28, borderRadius:'50%', border:'none', cursor:'pointer', background:'transparent', padding:0 }} />
            </div>
          </div>

          <Divider label="Permissions" />

          <div style={{ display:'flex', gap:10, margin:'14px 0', alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ fontSize:12.5, color:'var(--text-muted)', flex:1 }}>
              <strong style={{ color:roleForm.color||'var(--brand)', fontFamily:'var(--font-mono)' }}>{roleForm.perms.length}</strong>
              {' '}of <strong style={{ fontFamily:'var(--font-mono)' }}>{ALL_PERMS.length}</strong> permissions enabled
            </div>
            <Btn size="sm" variant="ghost" onClick={() => { setRoleForm(r=>({...r,perms:ALL_PERMS.map(p=>p.key)})); setRoleDirty(true); }}>Enable All</Btn>
            <Btn size="sm" variant="ghost" onClick={() => { setRoleForm(r=>({...r,perms:[]})); setRoleDirty(true); }}>Disable All</Btn>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:12, maxHeight:360, overflowY:'auto', paddingRight:2 }}>
            {GROUPS.map(group => {
              const groupPerms = ALL_PERMS.filter(p => p.group === group);
              const enabledCount = groupPerms.filter(p => roleForm.perms.includes(p.key)).length;
              const allOn = enabledCount === groupPerms.length;
              return (
                <div key={group} style={{ background:'var(--bg-elevated)', border:'1px solid var(--border-subtle)', borderRadius:'var(--radius-md)', overflow:'hidden' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 16px', borderBottom:'1px solid var(--border-subtle)', background:'var(--bg-hover)' }}>
                    <div style={{ flex:1 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>{group}</span>
                      <span style={{ fontSize:11.5, color:'var(--text-muted)', marginLeft:8 }}>{enabledCount}/{groupPerms.length}</span>
                    </div>
                    <button onClick={() => toggleGroup(group)} style={{
                      padding:'3px 12px', borderRadius:'var(--radius-full)', fontSize:11.5, fontWeight:600, cursor:'pointer', transition:'var(--transition)',
                      background: allOn ? 'var(--accent-green-dim)' : 'var(--bg-surface)',
                      color: allOn ? 'var(--accent-green)' : 'var(--text-muted)',
                      border:`1px solid ${allOn ? 'rgba(34,197,94,.2)' : 'var(--border-default)'}`,
                    }}>{allOn ? 'Disable All' : 'Enable All'}</button>
                  </div>
                  {groupPerms.map(perm => {
                    const on = roleForm.perms.includes(perm.key);
                    return (
                      <div key={perm.key} onClick={() => togglePerm(perm.key)}
                        style={{ display:'flex', alignItems:'center', gap:14, padding:'10px 16px', cursor:'pointer', transition:'background .1s', borderTop:'1px solid var(--border-subtle)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-surface)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <Toggle checked={on} onChange={() => togglePerm(perm.key)} />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600, color: on ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{perm.label}</div>
                          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{perm.desc}</div>
                        </div>
                        <span style={{ fontSize:10.5, fontFamily:'var(--font-mono)', color:'var(--text-muted)', flexShrink:0 }}>{perm.key}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:12, color:'var(--brand)' }}>{roleDirty && '● Unsaved changes'}</span>
            <div style={{ display:'flex', gap:10 }}>
              <Btn variant="ghost" onClick={() => setRoleModal(null)}>Cancel</Btn>
              <Btn onClick={saveRole}>{roleModal === 'new' ? 'Create Role' : 'Save Changes'}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
